import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Edit, 
  Eye, 
  Key, 
  Monitor, 
  Package,
  Calendar,
  User,
  Hash,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Shield
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { AssetCategory } from "@/types/asset";
import * as Icons from "lucide-react";

interface AssetsListProps {
  assets: any[];
  categories: AssetCategory[];
  onEdit: (asset: any) => void;
  showHistory?: boolean;
}

export function AssetsList({ assets, categories, onEdit, showHistory = false }: AssetsListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "assigned": return <CheckCircle className="w-4 h-4" />;
      case "returned": return <Clock className="w-4 h-4" />;
      case "lost": return <XCircle className="w-4 h-4" />;
      case "damaged": return <AlertTriangle className="w-4 h-4" />;
      case "expired": return <Clock className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case "assigned": return "bg-green-100 text-green-800 border-green-300";
      case "returned": return "bg-blue-100 text-blue-800 border-blue-300";
      case "lost": return "bg-red-100 text-red-800 border-red-300";
      case "damaged": return "bg-orange-100 text-orange-800 border-orange-300";
      case "expired": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };
  
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "assigned": return "مسلمة";
      case "returned": return "مُرجعة";
      case "lost": return "مفقودة";
      case "damaged": return "تالفة";
      case "expired": return "منتهية";
      default: return status;
    }
  };
  
  const getConditionColor = (condition: string) => {
    switch (condition) {
      case "new": return "bg-green-100 text-green-800";
      case "good": return "bg-blue-100 text-blue-800";
      case "fair": return "bg-yellow-100 text-yellow-800";
      case "poor": return "bg-orange-100 text-orange-800";
      case "damaged": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };
  
  const getConditionLabel = (condition: string) => {
    switch (condition) {
      case "new": return "جديد";
      case "good": return "جيد";
      case "fair": return "متوسط";
      case "poor": return "ضعيف";
      case "damaged": return "تالف";
      default: return condition;
    }
  };
  
  const getCategoryIcon = (iconName?: string) => {
    if (!iconName) return <Package className="w-5 h-5" />;
    const Icon = (Icons as any)[iconName];
    return Icon ? <Icon className="w-5 h-5" /> : <Package className="w-5 h-5" />;
  };
  
  if (assets.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">لا توجد عهد</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {assets.map((asset) => (
        <Card key={asset.id} className="hover:shadow-lg transition-shadow">
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: asset.category?.color ? `${asset.category.color}20` : "#f0f0f0" }}
                >
                  {getCategoryIcon(asset.category?.icon)}
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{asset.asset_name}</h3>
                  <p className="text-xs text-muted-foreground">{asset.category?.name_ar}</p>
                </div>
              </div>
              <Badge 
                variant="outline" 
                className={`text-xs ${getStatusColor(asset.status)}`}
              >
                {getStatusIcon(asset.status)}
                <span className="mr-1">{getStatusLabel(asset.status)}</span>
              </Badge>
            </div>
            
            <div className="space-y-2 text-sm">
              {asset.asset_code && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Hash className="w-3 h-3" />
                  <span className="text-xs">{asset.asset_code}</span>
                </div>
              )}
              
              {asset.employee && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-3 h-3" />
                  <span className="text-xs">{asset.employee.name}</span>
                </div>
              )}
              
              {asset.serial_number && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Monitor className="w-3 h-3" />
                  <span className="text-xs">{asset.serial_number}</span>
                </div>
              )}
              
              {asset.assigned_date && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  <span className="text-xs">
                    {format(new Date(asset.assigned_date), "dd MMM yyyy", { locale: ar })}
                  </span>
                </div>
              )}
              
              {asset.condition && asset.asset_type === "physical" && (
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${getConditionColor(asset.condition)}`}>
                    {getConditionLabel(asset.condition)}
                  </Badge>
                </div>
              )}
              
              {asset.asset_type === "credentials" && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Key className="w-3 h-3" />
                  <span className="text-xs">بيانات دخول</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(asset)}
              >
                <Eye className="w-4 h-4 ml-1" />
                عرض
              </Button>
              {asset.status === "assigned" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(asset)}
                >
                  <Edit className="w-4 h-4 ml-1" />
                  تعديل
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}